import { createHandler as initGriffin } from 'griffin-paypal/number';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

let griffin;
let currencyMetadata;
const MAX_AMOUNT_LENGTH = 9;
__initializeGriffin();

function __initializeGriffin() {
  try {
    if (!currencyMetadata) {
      __getCurrencyMetadata();
    }
    if (currencyMetadata && !griffin) {
      griffin = initGriffin(currencyMetadata);
    }
  } catch (err) {
    console.log(err);
  }
}

export function getGriffin() {
  return griffin;
}

function __getCurrencyMetadata() {
  const serverProps = document.getElementById('server-props');
  const serverPropsData = (serverProps && serverProps.innerHTML) || '{}';
  try {
    currencyMetadata = _get(JSON.parse(serverPropsData), 'currencyMetadata');
  } catch (err) {
    console.warn(err);
  }
}

function _getAmountValue(currencyParts) {
  var amount =
    currencyParts && currencyParts.find((item) => item.type === 'value');
  return amount && amount.value;
}

export function formatAmount(value, currency) {
  value = value.replace(/[^0-9]/g, '') || 0;
  value = parseInt(value, 10).toString();

  if (value.length === 1) {
    value = `00${value}`;
  } else if (value.length === 2) {
    value = `0${value}`;
  } else if (value.length > MAX_AMOUNT_LENGTH) {
    value = value.substr(0, MAX_AMOUNT_LENGTH);
  }

  const digits = _get(griffin, `metadata.currencyInfo[${currency}].digits`);
  const negativeDigits = `-${digits}`;

  // Some currencies (eg: JPY) do not have decimal points
  if (digits > 0) {
    value = `${value.slice(0, negativeDigits)}.${value.slice(negativeDigits)}`;
  }

  return formatCurrency({ value, currency });
}

export function formatAmountFromServer(value, currency) {
  const digits = _get(griffin, `metadata.currencyInfo[${currency}].digits`);
  const negativeDigits = `-${digits}`;
  const hasDecimalSeparator = value.indexOf('.') > 0;

  if (value.indexOf('.') < 0) {
    if (digits === 2) {
      value = `${value}.00`;
    }
    if (digits === 1) {
      value = `${value}.0`;
    }
  } else {
    const digitsAfterDecimalPoint = value.split('.')[1].length;
    if (digitsAfterDecimalPoint === 1 && digits === 2) {
      value = `${value}0`;
    }
  }

  value = value.replace(/[^0-9]/g, '') || 0;
  value = parseInt(value, 10).toString();

  if (value.length === 1) {
    value = `00${value}`;
  } else if (value.length === 2) {
    value = `0${value}`;
  } else if (value.length > MAX_AMOUNT_LENGTH) {
    value = value.substr(0, MAX_AMOUNT_LENGTH);
  }

  // Some currencies (eg: JPY) do not have decimal points
  if (digits > 0) {
    value = `${value.slice(0, negativeDigits)}.${value.slice(negativeDigits)}`;
  } else if (hasDecimalSeparator) {
    value = value.slice(0, value.length - 2);
  }

  return formatCurrency({ value, currency });
}

function formatCurrency(params) {
  if (!params.value || !params.currency) {
    return '';
  }

  if (!currencyMetadata) {
    return params.value;
  }

  let response = griffin.formatCurrency(
    {
      value: params.value,
      currency: params.currency,
    },
    griffin.CURRENCY_FORMAT.parts()
  );

  return _getAmountValue(response);
}

export function normalizeNumber(amount) {
  if (!amount) {
    return '';
  }

  return griffin.normalizeNumber(amount);
}

export function formatCurrencyAsISOFormat(params) {
  return griffin.formatCurrency(
    params,
    griffin.CURRENCY_FORMAT_SYMBOLISOCURRENCY
  );
}

export function formatCurrencyNoDecimals(params, format) {
  if (!params.value) {
    return '';
  }

  var options = format ? format : griffin.CURRENCY_FORMAT_SYMBOLISOCURRENCY;
  options.maximumFractionDigits = 0;

  let formattedCurrency = griffin?.formatCurrency(params, options);

  return formattedCurrency;
}

export function displayCurrency(amount, currency, { noDecimals = false } = {}) {
  const formattedAmount = griffin.formatCurrency({ currency, value: amount });
  const digits = _get(griffin, `metadata.currencyInfo[${currency}].digits`);
  const negativeDigits = `-${digits}`;

  if (
    amount !== 0 &&
    noDecimals &&
    formattedAmount.slice(negativeDigits) === '00'
  ) {
    const currencyParams = {
      currency: currency,
      value: parseFloat(amount),
    };

    return formatCurrencyNoDecimals(currencyParams, griffin.CURRENCY_FORMAT);
  }

  return formattedAmount;
}

export function displayPercentage(amount) {
  return griffin.formatNumber(amount, { style: 'percent' });
}

export function formatNumberDecimals(value) {
  if (!value) {
    return '';
  }

  var options = griffin.NUMBER_FORMAT;
  options.maximumFractionDigits = 2;

  return griffin.formatNumber(value, options);
}

export function getCurrencyParts(amount, currencyCode) {
  return griffin.formatCurrency(
    {
      currency: currencyCode,
      value: amount,
    },
    griffin.CURRENCY_FORMAT_SYMBOLISOCURRENCY.parts()
  );
}

export function getCurrencyPartByType(currencyParts, type) {
  const currencyPart =
    currencyParts && currencyParts.find((item) => item.type === type);

  return currencyPart && currencyPart.value;
}

export function formatAmountToOnlyNumbers(amount) {
  if (_isEmpty(amount)) {
    amount = '0';
  }

  return normalizeNumber(amount);
}

export const MIN_INPUT_WIDTH = 91;
export const DEFAULT_INPUT_FONTSIZE = 44;
const DIGITS_TO_FONT_SIZE_MAPPING = {
  8: 42,
  9: 40,
  10: 38,
};

export function getFontSize(value, isMobile) {
  let digitCount = value.replace(/[\.,]/g, '').length;

  if (digitCount < 8) {
    return DEFAULT_INPUT_FONTSIZE;
  }

  return DIGITS_TO_FONT_SIZE_MAPPING[`${digitCount}`] || 44;
}

export function getInputWidth(field, text) {
  text = text || '0.00';

  // Get styles from the element
  const currentStyles = window.getComputedStyle(field) || {};
  let { fontWeight } = currentStyles;
  let fontSize = `${getFontSize(text)}px`;

  // Create a span element outside visible area
  const spanElem = document.createElement('span');
  spanElem.style.fontSize = fontSize;
  spanElem.style.fontFamily = 'PayPalSansBig-Regular';
  spanElem.style.fontWeight = fontWeight || '400';
  spanElem.style.position = 'fixed';
  spanElem.style.top = '-1000px';
  spanElem.style.left = '-1000px';
  spanElem.innerHTML = text;
  document.body.appendChild(spanElem);

  // Get width of the span and then remove it
  const width = spanElem.getBoundingClientRect().width + 2; // increase by 2px to fix a layout problem in Safari iOS browser.
  document.body.removeChild(spanElem);

  if (width < MIN_INPUT_WIDTH) {
    return MIN_INPUT_WIDTH;
  }
  return width;
}
